import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell, FaMapMarkerAlt } from "react-icons/fa";
import { useNotifications } from "./NotificationsContext";
import { NOTIF_LIMIT } from "../utils/notifications";

function timeAgo(ts) {
  const ms = ts?.toMillis
    ? ts.toMillis()
    : ts?.seconds
      ? ts.seconds * 1000
      : 0;
  if (!ms) return "";
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Bell icon (sits next to the profile icon) with a red unread count, and a
// dropdown listing address-change notifications. Tapping one opens that event
// (where the new address shows and the notification is marked read).
const NotificationBell = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, clearAll } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close the dropdown on an outside tap.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const openNotification = (n) => {
    setOpen(false);
    // Don't mark read here - the event screen does that on open, so the address
    // banner still shows and the badge clears once the user actually sees it.
    if (n.code) navigate(`/event/${n.code}`);
  };

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        className="menu-button"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FaBell style={{ display: "block" }} />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              minWidth: "18px",
              height: "18px",
              padding: "0 4px",
              borderRadius: "9px",
              backgroundColor: "#e53935",
              color: "#fff",
              fontSize: "11px",
              fontWeight: 700,
              lineHeight: 1,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid var(--item-bg, #fff)",
            }}
          >
            {unreadCount >= NOTIF_LIMIT ? `${NOTIF_LIMIT}+` : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: "min(330px, 82vw)",
            // Cap the whole popup and let the LIST scroll inside it, so the
            // dropdown never grows past this height no matter how many arrive.
            maxHeight: "min(60vh, 460px)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--item-bg, #fff)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: "12px",
            boxShadow: "0 10px 28px rgba(0,0,0,0.20)",
            overflow: "hidden",
            zIndex: 1000,
          }}
        >
          {/* Fixed header. */}
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              borderBottom: "1px solid var(--border-color, #eee)",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: "14px" }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007bff",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Scrollable list - the only part that grows/scrolls. */}
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "20px 14px",
                  color: "var(--text-secondary, #777)",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                You're all caught up.
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openNotification(n)}
                  style={{
                    display: "flex",
                    gap: "10px",
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    border: "none",
                    borderBottom: "1px solid var(--border-color, #f0f0f0)",
                    backgroundColor: n.read
                      ? "transparent"
                      : "rgba(0,123,255,0.06)",
                    color: "var(--text-primary, #1a1a1a)",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{ marginTop: "2px", color: "#007bff", flexShrink: 0 }}
                  >
                    <FaMapMarkerAlt size={15} />
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                      style={{
                        display: "block",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      {n.eventName || "An event"}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: "13px",
                        color: "var(--text-secondary, #777)",
                        lineHeight: 1.35,
                      }}
                    >
                      Address changed{n.address ? ` to ${n.address}` : ""}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: "11px",
                        color: "var(--text-secondary, #999)",
                        marginTop: "2px",
                      }}
                    >
                      {timeAgo(n.createdAt)}
                    </span>
                  </span>
                  {!n.read && (
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "4px",
                        backgroundColor: "#e53935",
                        flexShrink: 0,
                        marginTop: "6px",
                      }}
                    />
                  )}
                </button>
              ))
            )}
          </div>

          {/* Fixed footer - clear everything currently shown. */}
          {notifications.length > 0 && (
            <div
              style={{
                flexShrink: 0,
                borderTop: "1px solid var(--border-color, #eee)",
                padding: "8px 14px",
                textAlign: "center",
              }}
            >
              <button
                type="button"
                onClick={clearAll}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-secondary, #777)",
                  cursor: "pointer",
                  fontSize: "12.5px",
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
